import { theme } from 'antd';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectBookingsByPersonID } from 'reduxStore/bookings/selectors';
import { Badge, Empty, Flex, Segmented, Typography } from 'zui/Antd';
import ActionDropdown from './ActionDropdown';

const { Text } = Typography;

const CurrentBookingsContent = ({ personID }) => {
	const {
		token: {
			fontWeightStrong,
			colorSuccess,
			paddingSM,
			colorBgLayout,
			borderRadius,
			colorBorderSecondary,
			colorText,
			paddingXS,
			fontSizeSM,
			colorTextLabel,
			paddingMD
		}
	} = theme.useToken();

	// state
	const [currentTab, setCurrentTab] = useState('Current');
	// ✅ Redux state
	const allBookings = useSelector((state) => selectBookingsByPersonID(state, personID));

	const { currentBookings, pastBookings, futureBookings } = useMemo(() => {
		const now = dayjs();
		const current = [];
		const past = [];
		const future = [];

		allBookings.forEach((booking) => {
			const startTime = dayjs(booking.startTime);
			const endTime = dayjs(booking.endTime);

			if (now.isBefore(startTime)) {
				future.push(booking);
			} else if (now.isAfter(endTime)) {
				past.push(booking);
			} else {
				current.push(booking);
			}
		});

		return { currentBookings: current, pastBookings: past, futureBookings: future };
	}, [allBookings]);

	// ✅ Get bookings based on selected tab
	const bookings = useMemo(() => {
		switch (currentTab) {
			case 'Current':
				return currentBookings;
			case 'Past':
				return pastBookings;
			case 'Future':
				return futureBookings;
			default:
				return currentBookings;
		}
	}, [currentTab, currentBookings, pastBookings, futureBookings]);

	const options = ['Past', 'Current', 'Future'];

	const handleSetSelectedTab = (value) => {
		setCurrentTab(value);
	};

	const formatDateRange = (startTime, endTime) => {
		const start = dayjs(startTime);
		const end = dayjs(endTime);
		return `${start.format('D MMMM YYYY')} - ${end.format('D MMMM YYYY')}`;
	};

	const calculateDaysRemaining = (endTime) => {
		const days = dayjs(endTime).diff(dayjs(), 'day');
		return `${days} ${days === 1 ? 'Day' : 'Days'} Remaining`;
	};

	return (
		<>
			<Segmented
				onChange={handleSetSelectedTab}
				value={currentTab}
				options={options}
				style={{ marginBottom: paddingMD }}
			/>
			<Flex gap={paddingXS}>
				{bookings.length === 0 ? (
					<Flex
						justify='center'
						align='center'
						style={{
							width: '100%'
						}}
					>
						<Empty
							description={`No ${currentTab.toLowerCase()} bookings exist`}
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					</Flex>
				) : (
					<Flex vertical gap={paddingXS} style={{ width: '100%' }}>
						{bookings.map((booking) => (
							<Flex
								key={booking.bookingID}
								style={{
									width: '100%',
									boxSizing: 'border-box',
									backgroundColor: colorBgLayout,
									borderRadius: borderRadius,
									border: `1px solid ${colorBorderSecondary}`,
									padding: paddingSM
								}}
								vertical
							>
								<Flex justify='space-between' align='center' style={{ width: '100%' }}>
									<Flex align='center' gap={4}>
										<Badge dot color={colorSuccess} />
										<Text style={{ color: colorText, fontWeight: fontWeightStrong }}>
											{booking?.name}
										</Text>
									</Flex>
									<Flex>
										<ActionDropdown deviceID={booking?.deviceID} bookingID={booking?.bookingID} />
									</Flex>
								</Flex>

								<Flex justify='space-between' align='center' style={{ width: '100%' }}>
									<Text
										style={{
											fontSize: fontSizeSM,
											color: colorTextLabel,
											fontWeight: 500
										}}
									>
										{booking?.bookingTypeName}
									</Text>
									<Text
										style={{
											fontSize: fontSizeSM,
											color: colorTextLabel,
											fontWeight: 500
										}}
									>
										{currentTab === 'Current'
											? calculateDaysRemaining(booking?.endTime)
											: formatDateRange(booking?.startTime, booking?.endTime)}
									</Text>
								</Flex>
							</Flex>
						))}
					</Flex>
				)}
			</Flex>
		</>
	);
};

export default CurrentBookingsContent;
